import React from 'react';
import styled from 'styled-components';
import { FlexMiddle } from '@ubeya/shared-web/components';
import { ReactComponent as BaseFilterIcon } from '@ubeya/shared-web/assets/filter.svg';
import { ReactComponent as BaseCloseIcon } from '@ubeya/shared-web/assets/close-small.svg';

const Wrapper = styled(FlexMiddle)`
  width: 100%;
  justify-content: space-between;
  gap: 10px;
  padding: 15px 12px;
  margin-bottom: 8px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.primaryLight};
`;

const Details = styled(FlexMiddle)`
  gap: 8px;
  min-width: 0;
  font-size: 13px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.black};
`;

const FilterIcon = styled(BaseFilterIcon)`
  width: 16px;
  min-width: 16px;
  height: 16px;

  path {
    fill: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const EmployeeName = styled.span`
  font-weight: 600;
`;

// Same shape as the SkeletonPulse blocks used around scheduling (inline-block, nbsp for height),
// but filled with a translucent white instead of grey1 - grey on the lavender banner reads as a
// rendering artifact rather than a placeholder. The pulse is what those blocks were reaching for
// with their leftover background-size and never got.
const SkeletonBlock = styled.span`
  display: inline-block;
  width: ${({ $width }) => $width};
  border-radius: 3px;
  background: ${({ theme }) => theme.colors.white}80;
  animation: filter-banner-skeleton-pulse 1.4s ease-in-out infinite;

  &::before {
    content: '\\00a0';
  }

  @keyframes filter-banner-skeleton-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.45;
    }
  }
`;

const ClearButton = styled.button.attrs(() => ({ type: 'button' }))`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary};

  &:hover {
    opacity: 0.8;
  }
`;

const CloseIcon = styled(BaseCloseIcon)`
  width: 14px;
  min-width: 14px;
  height: 14px;

  path {
    fill: ${({ theme }) => theme.colors.primary};
  }
`;

// Full-width strip between the modal title and the table header, shown while the requests list is
// pinned to specific requests.
// The three states of the "- {name}, {type}" tail are deliberately distinct: `summary` renders it,
// `isLoading` shows skeleton blocks in its place because the row is still on its way, and neither
// means the row is never coming (declined, or not in the status tab being shown) - there the count
// alone is still true, so the banner degrades to it rather than claiming the request is missing.
const RequestsFilterBanner = ({ t, count, summary, isLoading, onClear }) => (
  <Wrapper>
    <Details>
      <FilterIcon />

      <span>
        {t('filteredToRequest', { count })}

        {isLoading ? (
          <>
            {' — '}
            <SkeletonBlock $width="90px" />
            {', '}
            <SkeletonBlock $width="60px" />
          </>
        ) : (
          summary && (
            <>
              {' — '}
              <EmployeeName>{summary.employeeName}</EmployeeName>
              {summary.typeName ? `, ${summary.typeName}` : ''}
            </>
          )
        )}
      </span>
    </Details>

    <ClearButton onClick={onClear}>
      {t('clearFilter')}
      <CloseIcon />
    </ClearButton>
  </Wrapper>
);

export default RequestsFilterBanner;
